import type { AuthTokens, SessionResponse } from './types';

const ACCESS_TOKEN_KEY = 'zolotalk_access_token';
const REFRESH_TOKEN_KEY = 'zolotalk_refresh_token';
const TOKEN_EXPIRY_KEY = 'zolotalk_token_expiry';

/** A value that may be produced synchronously or via a promise. */
export type Awaitable<T> = T | Promise<T>;

/**
 * Token persistence seam shared by every platform. Methods are
 * {@link Awaitable} so a synchronous web/in-memory store and an asynchronous
 * mobile store (Android Keystore / iOS Keychain, both promise-based — see
 * `apps/mobile/src/auth/secure-token-store.ts`) satisfy one contract; the
 * {@link ApiClient} awaits every call. Persisting tokens only inside the
 * platform's encrypted store is the mobile store's responsibility
 * (REQ-MOB-003 AC-04); this interface is storage-agnostic by design.
 */
export interface TokenStore {
  getAccessToken: () => Awaitable<string | null>;
  getRefreshToken: () => Awaitable<string | null>;
  setTokens: (tokens: AuthTokens) => Awaitable<void>;
  clearTokens: () => Awaitable<void>;
  isAccessTokenExpired: () => Awaitable<boolean>;
}

/**
 * Projects a REQ-AUTH-001 {@link SessionResponse} onto the storage-facing
 * {@link AuthTokens}. Access-token lifetime comes from `accessTokenExpiresIn`
 * (seconds) — reading a non-existent `expiresIn` off the raw envelope was the
 * scaffold's latent refresh bug (NaN expiry ⇒ a token that never refreshed).
 */
export function sessionToAuthTokens(session: SessionResponse): AuthTokens {
  return {
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
    expiresIn: session.accessTokenExpiresIn,
  };
}

/**
 * Minimal `localStorage` shape. Reaching web storage through this typed
 * indirection — instead of the bare `localStorage`/`window` globals — keeps
 * this shared package free of a DOM lib dependency, so it also typechecks
 * inside the React Native app, which has no such globals. Mobile persists
 * tokens in the platform secure enclave instead (see `apps/mobile`
 * secure-token-store), never in web storage.
 */
interface WebStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function getWebStorage(): WebStorage | null {
  return (globalThis as { localStorage?: WebStorage }).localStorage ?? null;
}

export function createTokenStore(): TokenStore {
  return getWebStorage() ? createWebTokenStore() : createMemoryTokenStore();
}

export function createWebTokenStore(): TokenStore {
  const storage = getWebStorage();
  const isExpired = (): boolean => {
    const expiry = storage?.getItem(TOKEN_EXPIRY_KEY);
    if (!expiry) return true;
    return Date.now() >= parseInt(expiry, 10);
  };
  return {
    getAccessToken: () => (isExpired() ? null : (storage?.getItem(ACCESS_TOKEN_KEY) ?? null)),
    getRefreshToken: () => storage?.getItem(REFRESH_TOKEN_KEY) ?? null,
    setTokens: (tokens: AuthTokens) => {
      if (!storage) return;
      storage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
      storage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      storage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + tokens.expiresIn * 1000));
    },
    clearTokens: () => {
      storage?.removeItem(ACCESS_TOKEN_KEY);
      storage?.removeItem(REFRESH_TOKEN_KEY);
      storage?.removeItem(TOKEN_EXPIRY_KEY);
    },
    isAccessTokenExpired: isExpired,
  };
}

function createMemoryTokenStore(): TokenStore {
  let accessToken: string | null = null;
  let refreshToken: string | null = null;
  let expiry = 0;

  return {
    getAccessToken: () => (Date.now() >= expiry ? null : accessToken),
    getRefreshToken: () => refreshToken,
    setTokens: (tokens: AuthTokens) => {
      accessToken = tokens.accessToken;
      refreshToken = tokens.refreshToken;
      expiry = Date.now() + tokens.expiresIn * 1000;
    },
    clearTokens: () => {
      accessToken = null;
      refreshToken = null;
      expiry = 0;
    },
    isAccessTokenExpired: () => Date.now() >= expiry,
  };
}
